import React from 'react';
import { motion } from 'framer-motion';
import { HiExclamationTriangle, HiClock, HiEnvelope } from 'react-icons/hi2';

const AccountNotActivated = ({ userEmail, applicationStatus = 'pending' }) => {
  const getStatusMessage = () => {
    switch (applicationStatus) {
      case 'pending':
        return {
          title: 'Account Not Activated',
          message: 'Your account has not been activated yet. Please wait for approval.',
          icon: <HiClock className="h-12 w-12 text-orange-500" />,
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
        };
      case 'rejected':
        return {
          title: 'Account Not Activated',
          message: 'Your account has not been activated yet. Please contact your administrator.',
          icon: <HiExclamationTriangle className="h-12 w-12 text-red-500" />,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
        };
      default:
        return {
          title: 'Account Not Activated',
          message: 'Your account has not been activated yet. Please contact your administrator.',
          icon: <HiExclamationTriangle className="h-12 w-12 text-gray-500" />,
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
        };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-md rounded-2xl border-2 ${statusInfo.borderColor} ${statusInfo.bgColor} p-8 shadow-lg`}
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="mb-6 flex justify-center"
          >
            {statusInfo.icon}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`text-2xl font-bold ${statusInfo.textColor} mb-4`}
          >
            {statusInfo.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className={`text-sm ${statusInfo.textColor} mb-6 leading-relaxed`}
          >
            {statusInfo.message}
          </motion.p>

          {userEmail && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mb-6 flex items-center justify-center gap-2 text-sm text-gray-600"
            >
              <HiEnvelope className="h-4 w-4" />
              <span>{userEmail}</span>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <div className="text-xs text-gray-500">
              <p>• Check your email for updates</p>
              <p>• Contact support if you have questions</p>
              <p>• You will be notified when your account is ready</p>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => (window.location.href = '/landing')}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Return to Home
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AccountNotActivated;
